import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import apiClient from '@/api/client'

export interface AICopilotGate {
  /** Render the bubble. */
  show: boolean
  role: string
  /** users.id of the signed-in staff member ('' when unknown). */
  userId: string
  /** ai_nav_flags from GET /settings — what validateUiAction checks targets against. */
  flags: string[]
  /** ai_writes: the copilot may propose menu changes here (shown in its "what I can do" list). */
  writes: boolean
  /**
   * Hide the bubble for this user until the page reloads. The widget calls it
   * when the backend answers 403 `ai_disabled` / `ai_role_not_enabled`.
   */
  hide: () => void
}

function stringList(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []
}

/**
 * Pure gate derivation from the GET /settings map. The bubble renders only
 * when `feature_ai` is exactly true, the role is in `ai_roles`, and the
 * staff member is on an /admin screen.
 */
export function resolveAICopilotGate(
  all: Record<string, unknown> | undefined,
  role: string,
  pathname: string,
): { show: boolean; flags: string[] } {
  const enabled = all?.feature_ai === true
  const roles = stringList(all?.ai_roles)
  const onAdmin = pathname === '/admin' || pathname.startsWith('/admin/')
  return {
    show: enabled && !!role && roles.includes(role) && onAdmin,
    flags: enabled ? stringList(all?.ai_nav_flags) : [],
  }
}

/** Whether the copilot may propose changes here: `ai_writes` exactly true. */
export function resolveAIWrites(all: Record<string, unknown> | undefined): boolean {
  return all?.feature_ai === true && all?.ai_writes === true
}

/** The signed-in user from localStorage (`pos_user`), as routes/admin.tsx reads it. */
function storedUser(): { id: string; role: string } {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return { id: '', role: '' }
    const u = JSON.parse(raw) as { id?: unknown; role?: unknown }
    return {
      id: typeof u.id === 'string' ? u.id : '',
      role: typeof u.role === 'string' ? u.role : '',
    }
  } catch {
    return { id: '', role: '' }
  }
}

/**
 * useAICopilotGate decides whether the Bhookly AI bubble renders. Reads the
 * server-injected `feature_ai` / `ai_roles` / `ai_nav_flags` keys from
 * GET /settings and shares the `['settings','all']` cache — no extra call.
 */
export function useAICopilotGate(): AICopilotGate {
  const { pathname } = useLocation()
  const onAdmin = pathname === '/admin' || pathname.startsWith('/admin/')
  const authed = apiClient.isAuthenticated()
  const { data } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
    // Only on signed-in /admin screens: elsewhere (login, the public scan and
    // password pages) a GET /settings without a token would 401 for nothing.
    enabled: authed && onAdmin,
  })
  const user = authed ? storedUser() : { id: '', role: '' }
  const [hiddenFor, setHiddenFor] = useState<string | null>(null)
  const hiddenKey = `${user.id}:${user.role}`
  const hide = useCallback(() => setHiddenFor(hiddenKey), [hiddenKey])

  const all = data?.data as Record<string, unknown> | undefined
  const gate = resolveAICopilotGate(all, user.role, pathname)
  return {
    show: gate.show && hiddenFor !== hiddenKey,
    role: user.role,
    userId: user.id,
    flags: gate.flags,
    writes: resolveAIWrites(all),
    hide,
  }
}

/**
 * Whether the admin dashboard shows its "AI copilot" developer tab: the deploy
 * has AI_DEV_DASHBOARD=1 (server-injected `feature_ai_dev_dashboard`) and the
 * signed-in user is an admin. Restaurant deploys never set the flag, so their
 * dashboard is unchanged.
 */
export function resolveAIDevDashboardGate(all: Record<string, unknown> | undefined, role: string): boolean {
  return all?.feature_ai_dev_dashboard === true && role === 'admin'
}

/**
 * useAIDevDashboardGate shares the `['settings','all']` cache, but refetches it
 * whenever the dashboard opens: the copilot gate keeps that query fresh forever
 * (staleTime Infinity), so a flag switched on while a tab was open would
 * otherwise stay hidden until a full reload.
 */
export function useAIDevDashboardGate(): boolean {
  const authed = apiClient.isAuthenticated()
  const { data } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: authed,
  })
  return resolveAIDevDashboardGate(data?.data as Record<string, unknown> | undefined, authed ? storedUser().role : '')
}
